import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import { barcodeProblem } from "@/lib/qrHelpers";

/**
 * Shared code-generation logic, used directly by BarcodeGenerator.tsx,
 * QrGenerator.tsx, and Book Writing Toolbox's back cover -- the same
 * real generation logic in all three places, not a separate
 * reimplementation for the "embedded in another tool" case. This is
 * also a deliberate small-scale test of the tool-portability idea
 * behind the API/Embed/MCP roadmap item: if a tool's core logic
 * can't cleanly serve both its own UI and another tool calling it
 * directly, it isn't actually portable yet, no matter what the
 * roadmap doc says.
 */

export const BARCODE_FORMATS = [
  { value: "CODE128", label: "Code 128 (letters, numbers and symbols, no accents)" },
  { value: "EAN13", label: "EAN-13 (13-digit retail)" },
  { value: "EAN8", label: "EAN-8 (8-digit retail)" },
  { value: "UPC", label: "UPC-A (12-digit retail)" },
  { value: "CODE39", label: "Code 39 (alphanumeric, industrial)" },
  { value: "ITF14", label: "ITF-14 (14-digit shipping/case)" },
] as const;

export const PRINT_DPI = 300;
export const DEFAULT_BAR_HEIGHT = 80;
/** Bar height in px at 1x (the barcode's own height, not counting the digits under it). */
export const clampBarHeight = (h?: number) => (h && Number.isFinite(h) ? Math.max(20, Math.min(200, Math.round(h))) : DEFAULT_BAR_HEIGHT);
export type BarcodeFormat = (typeof BARCODE_FORMATS)[number]["value"];

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
  return t;
})();
function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** Returns a copy of a PNG with a pHYs chunk (pixels per metre) so print apps know the intended resolution. */
export function setPngDpi(png: Uint8Array, dpi: number): Uint8Array {
  const sig = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (png.length < 33 || sig.some((b, i) => png[i] !== b)) return png;
  const ppm = Math.round(dpi / 0.0254);
  const chunk = new Uint8Array(21); // length(4) + type(4) + data(9) + crc(4)
  const dv = new DataView(chunk.buffer);
  dv.setUint32(0, 9);
  chunk.set([0x70, 0x48, 0x59, 0x73], 4); // "pHYs"
  dv.setUint32(8, ppm); dv.setUint32(12, ppm); chunk[16] = 1;
  dv.setUint32(17, crc32(chunk.subarray(4, 17)));
  // Drop any existing pHYs, then insert after IHDR (8-byte signature + 25-byte IHDR chunk).
  const parts: Uint8Array[] = [png.subarray(0, 33), chunk];
  let i = 33;
  while (i + 12 <= png.length) {
    const len = new DataView(png.buffer, png.byteOffset + i, 4).getUint32(0);
    const type = String.fromCharCode(png[i + 4], png[i + 5], png[i + 6], png[i + 7]);
    const end = i + 12 + len;
    if (type !== "pHYs") parts.push(png.subarray(i, end));
    i = end;
  }
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0; for (const p of parts) { out.set(p, o); o += p.length; }
  return out;
}

function canvasToPngBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error("Couldn't render the barcode image")); return; }
      blob.arrayBuffer().then((buf) => resolve(new Uint8Array(buf)));
    }, "image/png");
  });
}

/**
 * Renders a REAL, scannable barcode (Code 128, EAN-13, UPC, etc.) via
 * jsbarcode -- not a decorative pattern. An earlier version of this
 * tool drew bars based on character codes with no real symbology behind
 * them; nothing generated that way would ever scan with an actual
 * barcode reader. jsbarcode implements the real encoding tables/check
 * digits each format requires.
 *
 * `caption` is optional human-readable context ("what this barcode is
 * for" -- a product name, an ISBN's title, etc.) drawn above the
 * barcode and composited into the SAME returned image, so it travels
 * with the barcode wherever it's used, rather than being a label that
 * only exists in this tool's own on-page UI.
 */
export async function generateBarcodePng(
  value: string,
  format: BarcodeFormat,
  opts: { caption?: string; displayValue?: boolean; scale?: number; dpi?: number; barHeight?: number } = {}
): Promise<Uint8Array> {
  if (!value.trim()) throw new Error("Enter a value to encode first");
  const scale = Math.max(1, Math.min(8, opts.scale ?? 1));
  const problem = barcodeProblem(value, format);
  if (problem) throw new Error(problem);

  const barcodeCanvas = document.createElement("canvas");
  let isValid = true;
  JsBarcode(barcodeCanvas, value.trim(), {
    format,
    displayValue: opts.displayValue ?? true,
    width: 2 * scale,
    height: clampBarHeight(opts.barHeight) * scale,
    margin: 10 * scale,
    fontSize: 20 * scale,
    valid: (ok) => { isValid = ok; },
  });
  if (!isValid) {
    throw new Error(`"${value}" can't be encoded as ${format}. Check the length and characters this format allows.`);
  }

  const finish = async (c: HTMLCanvasElement) => {
    const bytes = await canvasToPngBytes(c);
    return opts.dpi ? setPngDpi(bytes, opts.dpi) : bytes;
  };
  if (!opts.caption?.trim()) {
    return finish(barcodeCanvas);
  }

  const combined = document.createElement("canvas");
  const captionHeight = 26 * scale;
  combined.width = barcodeCanvas.width;
  combined.height = barcodeCanvas.height + captionHeight;
  const ctx = combined.getContext("2d")!;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, combined.width, combined.height);
  ctx.fillStyle = "#111111";
  ctx.font = `${14 * scale}px sans-serif`;
  ctx.textAlign = "center";
  ctx.fillText(opts.caption.trim(), combined.width / 2, 18 * scale);
  ctx.drawImage(barcodeCanvas, 0, captionHeight);
  return finish(combined);
}

/** Vector barcode (SVG text), same validation as the PNG path. */
export function generateBarcodeSvg(value: string, format: BarcodeFormat, opts: { displayValue?: boolean; barHeight?: number } = {}): string {
  if (!value.trim()) throw new Error("Enter a value to encode first");
  const problem = barcodeProblem(value, format);
  if (problem) throw new Error(problem);
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  let isValid = true;
  JsBarcode(svg, value.trim(), {
    format, displayValue: opts.displayValue ?? true, width: 2, height: clampBarHeight(opts.barHeight), margin: 10,
    valid: (ok) => { isValid = ok; },
  });
  if (!isValid) throw new Error(`"${value}" can't be encoded as ${format}. Check the length and characters this format allows.`);
  svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  return new XMLSerializer().serializeToString(svg);
}

/** Real QR generation via the `qrcode` package -- same logic
 *  QrGenerator.tsx uses for its own on-page preview, factored out here
 *  so Book Writing Toolbox's back cover calls the identical code path
 *  rather than a second implementation. */
export async function generateQrPng(text: string, opts: { size?: number } = {}): Promise<Uint8Array> {
  if (!text.trim()) throw new Error("Enter a URL or text to encode first");
  const dataUrl = await QRCode.toDataURL(text.trim(), { margin: 1, width: opts.size ?? 240 });
  const base64 = dataUrl.split(",")[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
